import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { ButtonBase } from '@mui';
import * as colors from '@constants/colors';

const Tab = ({
  buttonStyle,
  icon,
  index,
  label,
  onActive,
  onClick,
  selected,
  style,
  value,
  width,
  ...other
}) => {
  const tabRef = useRef({ props: { index, onActive, value } });

  // Keep ref updated with current props (needed for handleClick callback)
  tabRef.current.props = { index, onActive, value };

  const rootStyle = {
    color: selected ? colors.secondaryColor : '#666',
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    width,
    textTransform: 'uppercase',
    padding: 0,
    ...style
  };

  const buttonContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '3rem',
    alignItems: 'center',
    justifyContent: 'center',
    ...buttonStyle
  };

  const handleClick = useCallback((event) => {
    if (onClick) {
      onClick(tabRef.current.props.value, event, tabRef.current);
    }
  }, [onClick]);

  let iconElement;
  if (icon && React.isValidElement(icon)) {
    iconElement = React.cloneElement(icon, {
      style: {
        fontSize: 24,
        color: (icon.props && icon.props.style && icon.props.style.color) ?
          icon.props.style.color : rootStyle.color,
        marginBottom: label ? 5 : 0
      }
    });
  }

  return (
    <ButtonBase
      {...other}
      style={rootStyle}
      onClick={handleClick}
    >
      <div style={buttonContainerStyle}>
        {iconElement}
        {label}
      </div>
    </ButtonBase>
  );
};

Tab.propTypes = {
  /**
   * Override the inline-styles of the button element.
   */
  buttonStyle: PropTypes.object,
  /**
   * The css class name of the root element.
   */
  className: PropTypes.string,
  /**
   * Sets the icon of the tab, you can pass `FontIcon` or `SvgIcon` elements.
   */
  icon: PropTypes.node,
  /**
   * @ignore
   */
  index: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  /**
   * Sets the text value of the tab item to the string specified.
   */
  label: PropTypes.node,
  /**
   * Fired when the active tab changes by touch or tap.
   * Use this event to specify any functionality when an active tab changes.
   * For example - we are using this to route to home when the third tab becomes active.
   * This function will always recieve the active tab as it\'s first argument.
   */
  onActive: PropTypes.func,
  /**
   * @ignore
   * This property is overriden by the Tabs component.
   */
  onClick: PropTypes.func,
  /**
   * @ignore
   * Defines if the current tab is selected or not.
   * The Tabs component is responsible for setting this property.
   */
  selected: PropTypes.bool,
  /**
   * Override the inline-styles of the root element.
   */
  style: PropTypes.object,
  /**
   * If value prop passed to Tabs component, this value prop is also required.
   * It assigns a value to the tab so that it can be selected by the Tabs.
   */
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /**
   * @ignore
   * This property is overriden by the Tabs component.
   */
  width: PropTypes.string
};

Tab.muiName = 'Tab';

export default Tab;
